/**
 * Reactive taskCounts helper for components.
 *
 * Merges cached task data (from projectTasksCache) with server props,
 * preferring cached data when available for real-time updates.
 */

import type { Project } from './types';
import { getCachedTasks, subscribeToTaskCounts, type TaskCounts } from './stores/projectTasksCache';

const DEFAULT_COUNTS: TaskCounts = { total: 0, done: 0, todo: 0 };

/**
 * Get current taskCounts for a project.
 * Returns cached computed counts if available, otherwise falls back to server props.
 *
 * Use this for initial state in components.
 */
export function getTaskCounts(project: Project): TaskCounts {
  const cached = getCachedTasks(project.id);
  if (cached.fetched && cached.tasks.length > 0) {
    const done = cached.tasks.filter((t) => t.status !== 'todo').length;
    const todo = cached.tasks.filter((t) => t.status === 'todo').length;
    return { total: cached.tasks.length, done, todo };
  }
  // Handle case where cache has fetched but project has no tasks
  if (cached.fetched) {
    return { total: 0, done: 0, todo: 0 };
  }
  return project.taskCounts ?? DEFAULT_COUNTS;
}

/**
 * Subscribe to taskCounts changes for a project.
 * Callback receives cached counts when available, otherwise project's server props.
 *
 * Usage in Svelte 5:
 * ```svelte
 * let taskCounts = $state(getTaskCounts(project));
 *
 * $effect(() => {
 *   return subscribeToProjectTaskCounts(project, (counts) => {
 *     taskCounts = counts;
 *   });
 * });
 * ```
 */
export function subscribeToProjectTaskCounts(
  project: Project,
  callback: (counts: TaskCounts) => void,
): () => void {
  const fallback = project.taskCounts ?? DEFAULT_COUNTS;

  return subscribeToTaskCounts(project.id, (cachedCounts) => {
    // Use cached counts if available, otherwise fall back to server props
    callback(cachedCounts ?? fallback);
  });
}

/**
 * Check if a project has any tasks (from cache or props)
 */
export function projectHasTasks(project: Project): boolean {
  const counts = getTaskCounts(project);
  return counts.total > 0;
}

/**
 * Get task completion percentage for a project
 */
export function getTaskCompletionPct(project: Project): number {
  const counts = getTaskCounts(project);
  if (counts.total === 0) return 0;
  return Math.round((counts.done / counts.total) * 100);
}
